import { useId, type ComponentPropsWithRef, type ReactNode } from "react";

import { cn } from "../lib/cn";

export interface RadioProps extends Omit<ComponentPropsWithRef<"input">, "type"> {
  label?: ReactNode;
  description?: ReactNode;
}

/**
 * Bouton radio. Même construction que `Checkbox` : natif masqué + pastille
 * dessinée par-dessus.
 *
 * Les radios d'un même choix doivent partager le même `name` — c'est ce qui
 * crée le groupe côté navigateur, et ce qui fait fonctionner les flèches du
 * clavier. Regroupez-les dans un `RadioGroup`.
 */
export function Radio({ className, label, description, id, ...props }: RadioProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const dot = (
    <span className="relative inline-flex shrink-0 items-center justify-center">
      <input
        type="radio"
        id={inputId}
        className={cn(
          "peer size-4 cursor-pointer appearance-none rounded-full border border-border-strong bg-surface-raised shadow-xs inset-shadow-control touch-manipulation",
          "before:absolute before:-inset-3 before:content-[''] sm:before:hidden",
          "transform-gpu transition-[background-color,border-color,transform] duration-fast ease-spring",
          "checked:border-accent checked:bg-accent checked:inset-shadow-raised",
          "active:scale-90 active:duration-instant",
          "hover:border-fg-muted checked:hover:border-accent",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
          "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-50 disabled:active:scale-100",
          className,
        )}
        {...props}
      />
      {/* La pastille éclot depuis le centre avec un léger dépassement. Une
          transition (et non une keyframe) pour qu'elle se rétracte au décochage. */}
      <span className="pointer-events-none absolute size-1.5 scale-0 transform-gpu rounded-full bg-fg-on-accent transition-transform duration-normal ease-spring peer-checked:scale-100" />
    </span>
  );

  if (!label) return dot;

  return (
    <div className="flex items-start gap-2.5">
      <span className="flex h-5 items-center">{dot}</span>
      <span className="flex flex-col gap-0.5">
        <label htmlFor={inputId} className="cursor-pointer text-sm text-fg select-none">
          {label}
        </label>
        {description && <span className="text-xs text-fg-muted">{description}</span>}
      </span>
    </div>
  );
}

export interface RadioGroupProps extends Omit<ComponentPropsWithRef<"fieldset">, "children"> {
  /** Intitulé du groupe. Rendu en `<legend>` : c'est lui qui est annoncé avant les options. */
  legend?: ReactNode;
  orientation?: "vertical" | "horizontal";
  children: ReactNode;
}

/**
 * Regroupe des `Radio`. Le `<fieldset>`/`<legend>` natif est ce qui permet à un
 * lecteur d'écran d'annoncer « Formule, 2 sur 3 » au lieu d'un simple « 2 sur 3 ».
 */
export function RadioGroup({
  className,
  legend,
  orientation = "vertical",
  children,
  ...props
}: RadioGroupProps) {
  return (
    <fieldset className={cn("flex flex-col gap-2", className)} {...props}>
      {legend && <legend className="mb-1 text-sm font-medium text-fg">{legend}</legend>}
      <div className={cn("flex gap-3", orientation === "vertical" ? "flex-col" : "flex-wrap")}>
        {children}
      </div>
    </fieldset>
  );
}
